import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, GraduationCap, Check, X, BookOpen, ChevronRight } from "lucide-react";

const GRADES = [
  { id: "6", name: "6th Grade", group: "Middle School" },
  { id: "7", name: "7th Grade", group: "Middle School" },
  { id: "8", name: "8th Grade", group: "Middle School" },
  { id: "9", name: "9th Grade (Freshman)", group: "High School" },
  { id: "10", name: "10th Grade (Sophomore)", group: "High School" },
  { id: "11", name: "11th Grade (Junior)", group: "High School" },
  { id: "12", name: "12th Grade (Senior)", group: "High School" },
  { id: "college", name: "College", group: "Higher Education" },
  { id: "adult", name: "Adult Learner", group: "Adult" },
];

const TESTS = [
  { 
    id: "staar", 
    name: "STAAR Test", 
    icon: "⭐",
    subjects: [
      { id: "staar-math", name: "Math" },
      { id: "staar-reading", name: "Reading" },
      { id: "staar-writing", name: "Writing" },
      { id: "staar-science", name: "Science" },
      { id: "staar-social", name: "Social Studies" },
    ]
  },
  { 
    id: "sat", 
    name: "SAT", 
    icon: "📚",
    subjects: [
      { id: "sat-math", name: "Math" },
      { id: "sat-reading", name: "Reading" },
      { id: "sat-writing", name: "Writing & Language" },
    ]
  },
  { 
    id: "act", 
    name: "ACT", 
    icon: "🎓",
    subjects: [
      { id: "act-english", name: "English" },
      { id: "act-math", name: "Math" },
      { id: "act-reading", name: "Reading" },
      { id: "act-science", name: "Science" },
      { id: "act-writing", name: "Writing (Optional)" },
    ]
  },
  { 
    id: "asvab", 
    name: "ASVAB", 
    icon: "🛡️",
    subjects: [
      { id: "asvab-arithmetic", name: "Arithmetic Reasoning" },
      { id: "asvab-word", name: "Word Knowledge" },
      { id: "asvab-paragraph", name: "Paragraph Comprehension" },
      { id: "asvab-math", name: "Math Knowledge" },
      { id: "asvab-science", name: "General Science" },
      { id: "asvab-auto", name: "Auto & Shop Information" },
      { id: "asvab-mechanical", name: "Mechanical Comprehension" },
      { id: "asvab-electronics", name: "Electronics Information" },
    ]
  },
  { 
    id: "citizenship", 
    name: "US Citizenship", 
    icon: "🇺🇸",
    subjects: [
      { id: "cit-civics", name: "Civics" },
      { id: "cit-history", name: "US History" },
      { id: "cit-government", name: "Government" },
      { id: "cit-reading", name: "Reading" },
      { id: "cit-writing", name: "Writing" },
    ]
  },
];

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [grade, setGrade] = useState("");
  const [gradeName, setGradeName] = useState("");
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showGradePicker, setShowGradePicker] = useState(false);
  const [showTestPicker, setShowTestPicker] = useState(false);
  const [showSubjectPicker, setShowSubjectPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleGradeSelect = (g) => {
    setGrade(g.id);
    setGradeName(g.name);
    setShowGradePicker(false);
  };

  const handleTestSelect = (test) => {
    setSelectedTest(test);
    setSelectedSubjects([]);
    setShowTestPicker(false);
    setShowSubjectPicker(true);
  };

  const toggleSubject = (subject) => {
    setSelectedSubjects(prev => {
      if (prev.find(s => s.id === subject.id)) {
        return prev.filter(s => s.id !== subject.id);
      } else {
        return [...prev, subject];
      }
    });
  };

  const handleSubjectsDone = () => {
    if (selectedSubjects.length === 0) {
      toast.error("Please select at least one subject");
      return;
    }
    setShowSubjectPicker(false);
  };

  const getSelectionSummary = () => {
    if (!selectedTest) return "Select test & subjects";
    if (selectedSubjects.length === 0) return `${selectedTest.name} - Select subjects`;
    return `${selectedTest.name}: ${selectedSubjects.map(s => s.name).join(", ")}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!grade) {
      toast.error("Please select your grade level");
      return;
    }
    if (!selectedTest || selectedSubjects.length === 0) {
      toast.error("Please select a test and subjects you need help with");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const topics = selectedSubjects.map(s => s.id).join(",");
      await register(name, email, password, grade, selectedTest.id, topics);
      toast.success("Account created! Let's start learning! 🚀");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="p-4">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 font-medium">
          <ArrowLeft className="w-5 h-5" />
          Back
        </Link>
      </header>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-lime-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-[0_4px_0_0_#65a30d]">
              <span className="text-white font-black text-3xl">C</span>
            </div>
            <h1 className="font-heading font-black text-3xl text-slate-800">Create Account</h1>
            <p className="text-slate-500 mt-2">Start your test prep journey today</p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_0_0_#f1f5f9] p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block font-bold text-slate-700 mb-2">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="name-input"
                  className="input-pop"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                  className="input-pop"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-2">
                  <span className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    Grade Level
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowGradePicker(true)}
                  data-testid="grade-select"
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 bg-slate-50 font-medium text-left flex items-center justify-between hover:border-indigo-300 transition-colors"
                >
                  <span className={grade ? "text-slate-700" : "text-slate-400"}>
                    {gradeName || "Select your grade"}
                  </span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-2">
                  <span className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    What do you need help with?
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowTestPicker(true)}
                  data-testid="topic-select"
                  className="w-full min-h-12 px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 font-medium text-left flex items-center justify-between hover:border-indigo-300 transition-colors"
                >
                  <span className={selectedTest ? "text-slate-700" : "text-slate-400"}>
                    {getSelectionSummary()}
                  </span>
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                </button>
                {selectedSubjects.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedSubjects.map(s => (
                      <span key={s.id} className="px-3 py-1 bg-lime-100 text-lime-700 text-sm font-medium rounded-full">
                        {s.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="password-input"
                    className="input-pop pr-12"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-sm text-slate-400 mt-1">At least 6 characters</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="register-btn"
                className="w-full btn-primary text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-500">
                Already have an account?{" "}
                <Link to="/login" className="font-bold text-indigo-500 hover:text-indigo-600">
                  Log In
                </Link>
              </p>
            </div>

            <p className="mt-4 text-xs text-slate-400 text-center">
              By creating an account, you agree to our{" "}
              <Link to="/terms" className="text-indigo-500 hover:underline">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/privacy" className="text-indigo-500 hover:underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>

      {/* Grade Picker Modal */}
      {showGradePicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowGradePicker(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[80vh] overflow-hidden animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-heading font-bold text-lg text-slate-800">Select Your Grade</h3>
              <button onClick={() => setShowGradePicker(false)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[60vh] p-2">
              {GRADES.map((g, index) => {
                const isSelected = grade === g.id;
                const showGroupHeader = index === 0 || GRADES[index - 1].group !== g.group;
                return (
                  <div key={g.id}>
                    {showGroupHeader && (
                      <div className="px-4 py-2 text-xs font-bold text-slate-400 uppercase mt-2 first:mt-0">{g.group}</div>
                    )}
                    <button
                      type="button"
                      onClick={() => handleGradeSelect(g)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${isSelected ? "bg-lime-100 text-lime-700" : "hover:bg-slate-50 text-slate-700"}`}
                    >
                      <span className="font-medium">{g.name}</span>
                      {isSelected && <Check className="w-5 h-5 text-lime-600" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Test Picker Modal */}
      {showTestPicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowTestPicker(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[80vh] overflow-hidden animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-heading font-bold text-lg text-slate-800">Select Your Test</h3>
              <button onClick={() => setShowTestPicker(false)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[60vh] p-4 space-y-3">
              {TESTS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => handleTestSelect(t)}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-slate-200 hover:border-indigo-300 hover:bg-slate-50 transition-all"
                >
                  <span className="text-3xl">{t.icon}</span>
                  <div className="text-left flex-1">
                    <p className="font-bold text-slate-700">{t.name}</p>
                    <p className="text-sm text-slate-500">{t.subjects.length} subjects</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Subject Picker Modal */}
      {showSubjectPicker && selectedTest && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowSubjectPicker(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[80vh] overflow-hidden animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div>
                <h3 className="font-heading font-bold text-lg text-slate-800">{selectedTest.name}</h3>
                <p className="text-sm text-slate-500">Select subjects you need help with</p>
              </div>
              <button onClick={() => setShowSubjectPicker(false)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[50vh] p-4 space-y-2">
              {selectedTest.subjects.map((subject) => {
                const isSelected = selectedSubjects.find(s => s.id === subject.id);
                return (
                  <button
                    key={subject.id}
                    type="button"
                    onClick={() => toggleSubject(subject)}
                    className={`w-full flex items-center justify-between px-4 py-4 rounded-xl border-2 transition-all ${
                      isSelected 
                        ? "bg-lime-50 border-lime-400" 
                        : "border-slate-200 hover:border-indigo-300"
                    }`}
                  >
                    <span className={`font-medium ${isSelected ? "text-lime-700" : "text-slate-700"}`}>
                      {subject.name}
                    </span>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? "bg-lime-500 border-lime-500" : "border-slate-300"
                    }`}>
                      {isSelected && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="p-4 border-t border-slate-100 space-y-2">
              <p className="text-sm text-slate-500 text-center">
                {selectedSubjects.length} subject{selectedSubjects.length !== 1 ? "s" : ""} selected
              </p>
              <button 
                onClick={handleSubjectsDone} 
                className="w-full btn-primary"
                disabled={selectedSubjects.length === 0}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
